import React from 'react';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AmbienceVolumeSlider from '@features/Admin/AmbienceSettings/ui/AmbienceVolumeSlider';
import { clampPercentage } from '@features/Admin/AmbienceSettings/utils/ambienceSettings.utils';
import { BackgroundSoundType } from '@shared/services/apiService/apiService';
import { useStyles } from './styles';
import { FormErrors, SoundFormState } from './ambienceSoundForm.utils';

type AmbienceSoundDialogProps = {
  open: boolean;
  isEditMode: boolean;
  isSaving: boolean;
  isReplaceFileDisabled: boolean;
  title: string;
  form: SoundFormState;
  errors: FormErrors;
  selectedFileName: string;
  previewUrl?: string | null;
  isPreviewPlaying: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onClose: () => void;
  onSave: () => void;
  onFieldChange: <K extends keyof SoundFormState>(key: K, value: SoundFormState[K]) => void;
  onPreviewToggle: () => void;
};

export const AmbienceSoundDialog: React.FC<AmbienceSoundDialogProps> = ({
  open,
  isEditMode,
  isSaving,
  isReplaceFileDisabled,
  title,
  form,
  errors,
  selectedFileName,
  previewUrl,
  isPreviewPlaying,
  fileInputRef,
  onClose,
  onSave,
  onFieldChange,
  onPreviewToggle,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <TextField
          label={t('superAdminManageAmbienceSounds.form.name')}
          value={form.name}
          onChange={(event) => onFieldChange('name', event.target.value)}
          error={Boolean(errors.name)}
          helperText={errors.name}
          fullWidth
        />
        <TextField
          label={t('superAdminManageAmbienceSounds.form.type')}
          value={form.type}
          onChange={(event) => onFieldChange('type', event.target.value as BackgroundSoundType)}
          disabled={isEditMode}
          fullWidth
          select>
          <MenuItem value="ambient">
            {t('superAdminManageAmbienceSounds.table.typeAmbient')}
          </MenuItem>
          <MenuItem value="thinking">
            {t('superAdminManageAmbienceSounds.table.typeThinking')}
          </MenuItem>
        </TextField>
        <Box className={classes.volumeRow}>
          <Typography variant="body2">
            {t('superAdminManageAmbienceSounds.form.defaultVolume')}
          </Typography>
          <Box className={classes.volumeControlsRow}>
            <Box className={classes.previewRow}>
              <Typography variant="body2">
                {t('superAdminManageAmbienceSounds.table.preview')}
              </Typography>
              <IconButton color="primary" disabled={!previewUrl} onClick={onPreviewToggle}>
                {isPreviewPlaying ? <StopIcon /> : <PlayArrowIcon />}
              </IconButton>
            </Box>
            <AmbienceVolumeSlider
              min={0}
              max={100}
              step={1}
              value={form.defaultVolume}
              valueLabelDisplay="auto"
              valueLabelFormat={(sliderValue) => `${sliderValue}%`}
              onChange={(_, nextValue) => {
                const nextVolume = clampPercentage(
                  Array.isArray(nextValue) ? nextValue[0] : nextValue,
                );
                onFieldChange('defaultVolume', nextVolume);
              }}
            />
          </Box>
          <Typography variant="caption" className={classes.fileHint}>
            {t('superAdminManageAmbienceSounds.form.defaultVolumeHint')}
          </Typography>
        </Box>
        <Box>
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp3,.wav,.ogg,audio/mpeg,audio/mp3,audio/wav,audio/ogg"
            className={classes.hiddenInput}
            onChange={(event) => onFieldChange('file', event.target.files?.[0] ?? null)}
          />
          <Box className={classes.fileRow}>
            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              disabled={isReplaceFileDisabled}>
              {isEditMode
                ? t('superAdminManageAmbienceSounds.form.replaceFile')
                : t('superAdminManageAmbienceSounds.form.selectFile')}
            </Button>
            <Typography variant="body2" className={classes.fileName}>
              {selectedFileName}
            </Typography>
          </Box>
          <Typography variant="caption" className={classes.fileHint}>
            {isEditMode
              ? t('superAdminManageAmbienceSounds.form.editFileHint')
              : t('superAdminManageAmbienceSounds.form.fileHint')}
          </Typography>
          {errors.file ? (
            <Typography variant="caption" color="error">
              {errors.file}
            </Typography>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={onSave} variant="contained" disabled={isSaving}>
          {isSaving ? <CircularProgress size={20} color="inherit" /> : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AmbienceSoundDialog;
